import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { cancellationRulesApi } from "@/dashboard/inventory_management/cancel/cancellationRulesApi";
import type {
  CancellationRuleEvaluationRequest,
  CancellationRuleWarning,
  RuleEvaluationStatement,
  StatementRuleEvaluation,
} from "@/dashboard/inventory_management/cancel/cancellationRulesTypes";
import type { IndividualCancelStatement, PortfolioItemTransfer } from "@/types";
import { debounce } from "lodash-es";
import {
  type MaybeRefOrGetter,
  computed,
  onBeforeUnmount,
  ref,
  shallowRef,
  toValue,
  watch,
} from "vue";

const EVALUATION_DEBOUNCE_MS = 400;
/** Mirrors the backend's `MAX_EVALUATED_PORTFOLIO_ITEMS`: a larger selection would fail validation on every edit. */
export const MAX_EVALUATED_PORTFOLIO_ITEMS = 5000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The fields of a statement, in its submit shape, that the rules read. */
export type EvaluatedStatement = Pick<
  IndividualCancelStatement,
  | "temp_id"
  | "registry_country_region"
  | "mwh_cancelled"
  | "is_any_aib"
  | "period_start"
  | "period_end"
>;

/**
 * The key a statement's warnings are held under: its `temp_id`, which stays the same across edits, so a response's
 * warnings stay with their statement when rows are added or removed while it is in flight. The position stands in
 * for a statement without one.
 */
export const ruleWarningKey = (
  statement: EvaluatedStatement,
  index: number
): string => statement.temp_id ?? String(index);

/** `statements[i]` answers the statement `keys[i]`. */
interface EvaluatedDraft {
  keys: string[];
  statements: StatementRuleEvaluation[];
}

/**
 * Evaluates the cancellation rules on a draft whenever its selection or a statement's region, MWh or period
 * changes. Requests are debounced, and a response that a newer request has overtaken is dropped. Failures go
 * to the global error handler; the last answer stays shown, since warnings never block submitting.
 *
 * `warningsByKey` holds each statement's warnings by its `ruleWarningKey`: a statement added since the request was
 * sent has no entry yet, the rest keep theirs until the next answer.
 *
 * `selectionTooLarge` is set while more than `MAX_EVALUATED_PORTFOLIO_ITEMS` rows are selected. Nothing is evaluated
 * then and no warnings are shown, so the caller should say the rules aren't checked.
 */
export const useCancelRuleEvaluation = (options: {
  companyId: MaybeRefOrGetter<number>;
  statements: MaybeRefOrGetter<EvaluatedStatement[]>;
  portfolioItems: MaybeRefOrGetter<PortfolioItemTransfer[]>;
  enabled: MaybeRefOrGetter<boolean>;
}) => {
  const { globalErrorHandler } = useGlobalHandlers();
  const evaluated = shallowRef<EvaluatedDraft>({ keys: [], statements: [] });
  const isEvaluating = ref(false);
  let latestRequestId = 0;

  const request = computed<CancellationRuleEvaluationRequest>(() => ({
    statements: toValue(options.statements).map(toEvaluationStatement),
    portfolio_items: toValue(options.portfolioItems).filter(
      (item) => item.selected_count > 0 && item.selected_count <= item.count
    ),
  }));

  const selectionTooLarge = computed(
    () => request.value.portfolio_items.length > MAX_EVALUATED_PORTFOLIO_ITEMS
  );

  // Serialized so edits the rules don't read (beneficiary name, address) don't trigger a request. The keys count
  // too: rows replaced by identical ones under new ids (an AI re-upload) would otherwise keep no warnings.
  const requestFingerprint = computed(() =>
    toValue(options.enabled)
      ? JSON.stringify({
          keys: toValue(options.statements).map(ruleWarningKey),
          request: request.value,
        })
      : null
  );

  const evaluate = async () => {
    const requestId = ++latestRequestId;
    const keys = toValue(options.statements).map(ruleWarningKey);
    const body = request.value;
    if (
      selectionTooLarge.value ||
      body.statements.every((s) => !s.registry_country_region)
    ) {
      evaluated.value = { keys, statements: [] };
      isEvaluating.value = false;
      return;
    }
    isEvaluating.value = true;
    try {
      const response = await cancellationRulesApi.evaluate(
        toValue(options.companyId),
        body
      );
      if (requestId !== latestRequestId) return;
      evaluated.value = { keys, statements: response.data.statements };
    } catch (error) {
      if (requestId !== latestRequestId) return;
      globalErrorHandler(error);
    } finally {
      if (requestId === latestRequestId) isEvaluating.value = false;
    }
  };

  const debouncedEvaluate = debounce(evaluate, EVALUATION_DEBOUNCE_MS);

  watch(
    requestFingerprint,
    (fingerprint) => {
      if (fingerprint === null) {
        debouncedEvaluate.cancel();
        return;
      }
      debouncedEvaluate();
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    debouncedEvaluate.cancel();
    latestRequestId++;
  });

  const warningsByKey = computed<Record<string, CancellationRuleWarning[]>>(
    () => {
      const { keys, statements } = evaluated.value;
      return Object.fromEntries(
        keys.map((key, index) => [key, statements[index]?.warnings ?? []])
      );
    }
  );

  return { warningsByKey, isEvaluating, selectionTooLarge };
};

const toEvaluationStatement = (
  statement: EvaluatedStatement
): RuleEvaluationStatement => {
  const mwh = Math.floor(Number(statement.mwh_cancelled));
  return {
    registry_country_region: statement.registry_country_region || null,
    mwh_cancelled: Number.isFinite(mwh) && mwh > 0 ? mwh : 0,
    is_any_aib: statement.is_any_aib ?? false,
    period_start: isoDateOrNull(statement.period_start),
    period_end: isoDateOrNull(statement.period_end),
  };
};

const isoDateOrNull = (value: string | null | undefined): string | null =>
  value && ISO_DATE.test(value) ? value : null;
